/** The longest side a vision board picture needs; phone photos are three to four times this. */
export const VISION_IMAGE_MAX_EDGE = 1600;
/** Below this a PNG or JPEG goes up as it is, whatever its dimensions. */
const SMALL_ENOUGH_BYTES = 900 * 1024;
const INK = '#10201A';

/**
 * Shrink a photo before it leaves the phone. A modern camera photo is 3 to 6 MB
 * and 4000 px wide, for a tile the board shows at a few hundred pixels; sending
 * it whole wastes the member's data and the tenant's storage.
 *
 * The result is a JPEG at most `VISION_IMAGE_MAX_EDGE` on its longest side, with
 * transparent areas laid on the app's ink so a PNG logo does not turn black.
 * `createImageBitmap` honours the photo's EXIF orientation. Anything the browser
 * cannot decode here (a HEIC on a browser without it, a corrupt file) goes up
 * untouched and the server decides, so a helper that fails never blocks a
 * member who could otherwise upload.
 */
export async function prepareVisionImage(file: File): Promise<Blob> {
  const plain = file.type === 'image/jpeg' || file.type === 'image/png';
  if (plain && file.size <= SMALL_ENOUGH_BYTES) return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, VISION_IMAGE_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.fillStyle = INK;
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.86),
    );
    return blob && (blob.size < file.size || !plain) ? blob : file;
  } catch {
    return file;
  }
}
